import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Link,
  Box
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { focusRingStyles } from "../utils/accessibility";

interface AccessibilityStatementProps {
  open: boolean;
  onClose: () => void;
  themeVariant: "light" | "dark";
}

/**
 * Accessibility Statement Dialog - EAA Mandatory
 * Required by European Accessibility Act (EAA) Article 4
 */
export const AccessibilityStatement: React.FC<AccessibilityStatementProps> = ({
  open,
  onClose,
  themeVariant
}) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="accessibility-statement-title"
      maxWidth={false}
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          background: themeVariant === "dark" ? "#121212" : "white",
          borderRadius: "8px"
        }
      }}
    >
      <DialogTitle id="accessibility-statement-title">
        {t("ROOSTER.PUBLIC-APP.ACCESSIBILITY_STATEMENT", {
          defaultValue: "Accessibility Statement"
        })}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ "& > *": { mb: 2 } }}>
          {/* Compliance Status */}
          <Typography variant="h6" component="h2">
            {t("ROOSTER.PUBLIC-APP.A11Y_COMPLIANCE_STATUS", {
              defaultValue: "Compliance Status"
            })}
          </Typography>
          <Typography variant="body1">
            {t("ROOSTER.PUBLIC-APP.A11Y_COMPLIANCE_TEXT", {
              defaultValue:
                "This scheduling application is designed to conform with WCAG 2.1 Level AA guidelines and the European Accessibility Act (EAA) via EN 301 549 standard."
            })}
          </Typography>

          {/* Accessible Features */}
          <Typography variant="h6" component="h2">
            {t("ROOSTER.PUBLIC-APP.A11Y_FEATURES", {
              defaultValue: "Accessible Features"
            })}
          </Typography>
          <Typography component="ul" sx={{ pl: 2 }}>
            <li>
              {t("ROOSTER.PUBLIC-APP.A11Y_FEATURE_KEYBOARD", {
                defaultValue:
                  "Full keyboard navigation support for all interactive elements"
              })}
            </li>
            <li>
              {t("ROOSTER.PUBLIC-APP.A11Y_FEATURE_SCREEN_READER", {
                defaultValue:
                  "Screen reader compatibility with ARIA labels and live announcements"
              })}
            </li>
            <li>
              {t("ROOSTER.PUBLIC-APP.A11Y_FEATURE_CONTRAST", {
                defaultValue:
                  "High contrast color schemes meeting WCAG 2.1 AA requirements"
              })}
            </li>
            <li>
              {t("ROOSTER.PUBLIC-APP.A11Y_FEATURE_MOTION", {
                defaultValue:
                  "Reduced motion support for users who prefer minimal animation"
              })}
            </li>
            <li>
              {t("ROOSTER.PUBLIC-APP.A11Y_FEATURE_FOCUS", {
                defaultValue: "Clear focus indicators for keyboard users"
              })}
            </li>
          </Typography>

          {/* Known Limitations */}
          <Typography variant="h6" component="h2">
            {t("ROOSTER.PUBLIC-APP.A11Y_LIMITATIONS", {
              defaultValue: "Known Limitations"
            })}
          </Typography>
          <Typography variant="body1">
            {t("ROOSTER.PUBLIC-APP.A11Y_LIMITATIONS_TEXT", {
              defaultValue:
                "Some third-party calendar components may have limited accessibility. We are actively working to improve these areas."
            })}
          </Typography>

          {/* Feedback */}
          <Typography variant="h6" component="h2">
            {t("ROOSTER.PUBLIC-APP.A11Y_FEEDBACK", {
              defaultValue: "Feedback"
            })}
          </Typography>
          <Typography variant="body1">
            {t("ROOSTER.PUBLIC-APP.A11Y_FEEDBACK_TEXT", {
              defaultValue:
                "If you encounter any accessibility barriers or have suggestions for improvement, please contact us."
            })}
          </Typography>

          {/* Contact - configurable via VITE_SUPPORT_EMAIL environment variable */}
          <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
            {t("ROOSTER.PUBLIC-APP.A11Y_CONTACT", {
              defaultValue: "Contact: "
            })}
            <Link
              href={`mailto:${import.meta.env.VITE_SUPPORT_EMAIL || "support@roosterinc.com"}`}
              sx={{ ...focusRingStyles }}
              aria-label={t("ROOSTER.PUBLIC-APP.A11Y_CONTACT_EMAIL", {
                defaultValue: "Email accessibility support"
              })}
            >
              {import.meta.env.VITE_SUPPORT_EMAIL || "support@roosterinc.com"}
            </Link>
          </Typography>

          {/* Last Updated - auto-generated at build time for EAA compliance tracking */}
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {t("ROOSTER.PUBLIC-APP.A11Y_LAST_UPDATED", {
              defaultValue: `Last updated: ${import.meta.env.VITE_A11Y_BUILD_DATE}`
            })}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          variant="text"
          sx={focusRingStyles}
          aria-label={t("ROOSTER.COMMON.CLOSE", { defaultValue: "Close" })}
        >
          {t("ROOSTER.COMMON.CLOSE", { defaultValue: "Close" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccessibilityStatement;
